import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ScrollableCurriculumTabStrip } from "@/components/ui/scrollable-curriculum-tab-strip";
import { Tabs, TabsContent, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { Fragment, useEffect, useState, type ReactNode } from "react";
import { BeltRank } from "../../MembersArea";

interface TaeguekPattern {
  number: number;
  name: string;
  meaning: string;
}

interface TaeguekProps {
  selectedBelt: BeltRank;
  getBeltColor: (belt: BeltRank) => string;
  patterns: TaeguekPattern[];
  currentPattern: number;
  hasDemo?: (aliases: string[]) => boolean;
  onWatchDemo?: (aliases: string[]) => void;
  /**
   * `curriculum` = broad demo link + horizontal tabs (main curriculum tab).
   * `legacy` (default) = accordion (dropdown).
   */
  presentation?: "legacy" | "curriculum";
  beltTabSurfaceClass?: string;
}

const DEFAULT_TAB_SURFACE = "!bg-gray-200 !text-gray-900";

const getGradingRequirements = (belt: BeltRank) => {
  if (
    ["BLACK/WHITE", "BLACK/D1", "BLACK/D2", "BLACK/D3", "BLACK/D4", "BLACK/D5"].includes(
      belt,
    )
  ) {
    return "Taegeuk 1, 7 & 8";
  }

  switch (belt) {
    case "YELLOW":
      return "Taegeuk 1";
    case "ORANGE":
      return "Taegeuk 1 & 2";
    case "BLUE":
      return "Taegeuk 1, 2 & 3";
    case "GREEN":
      return "Taegeuk 1, 3 & 4";
    case "PURPLE":
      return "Taegeuk 1, 4 & 5";
    case "BROWN":
      return "Taegeuk 1, 5 & 6";
    case "RED":
      return "Taegeuk 1, 6 & 7";
    case "NAVY":
      return "Taegeuk 1, 7 & 8";
    default:
      return "Taegeuk 1";
  }
};

const getTaeguekInstructions = (pattern: TaeguekPattern) => {
  if (pattern.number === 1) {
    return {
      description:
        "Taegeuk Il Jang represents the symbol of 'Keon', one of the 8 Kwaes (divination signs), which means the 'heaven and yang'. As the 'Keon' symbolizes the beginning of the creation of all things in the universe, so does the Taegeuk 1 Jang in the training of Taekwondo.",
      steps: [
        "Start in the Ready or **Joon Bi** stance",
        "Turn left 90 degrees into a walking stance with left foot forward, left low block",
        "Step forward into walking stance with right foot forward, right hand middle punch",
        "Turn right 180 degrees into a walking stance with right foot forward, right low block",
        "Step forward into walking stance with left foot forward, left hand middle punch",
        "Turn left 90 degrees into a front stance with left foot forward, left low block, right middle punch",
        "Turn right 90 degrees into a walking stance with right foot forward, left inside block",
        "Step forward into walking stance with left foot forward, right hand middle punch",
        "Turn left 180 degrees into a walking stance with left foot forward, right inside block",
        "Step forward into walking stance with right foot forward, left hand middle punch",
        "Turn right 90 degrees into a front stance with right foot forward, right low block, left middle punch",
        "Turn left 90 degrees into a walking stance with left foot forward, left high block",
        "Right front kick, foot placed down into walking stance with right foot forward, right middle punch",
        "Turn right 180 degrees into a walking stance with right foot forward, right high block",
        "Left front kick, foot placed down into walking stance with left foot forward, left middle punch",
        "Turn right 90 degrees into a front stance with left foot forward, left low block",
        "Step forward into a front stance with right foot forward, right middle punch **KIHAP!**",
        'When the Master/Instructor says "Bah Ro" (return), turn and face them.',
      ],
    };
  }
  if (pattern.number === 2) {
    return {
      description:
        "This is the second form taught to Taekwondo students. According to the World Taekwondo (WT), Taegeuk Ee Jang symbolizes the 'Tae', one of the 8 divination signs, which signifies the inner firmness and the outer softness.",
      steps: [
        "Start in the Ready or Joon Bi stance",
        "Turn 90 degrees to the left into a walking stance with left foot forward, left arm low block",
        "Step forward into front stance with right foot forward, right arm middle punch",
        "Turn 180 degrees to the right into walking stance with right foot forward, right arm low block",
        "Step forward into front stance with left foot forward, left arm middle punch",
        "Turn 90 degrees to the left into walking stance with left foot forward, right arm inner middle block",
        "Step forward into walking stance with right foot forward, left arm inner middle block",
        "Turn 90 degrees to the left into walking stance with left foot forward, left arm low block",
        "Right foot front snap kick, landing in front stance with right foot forward, right arm high punch",
        "Turn 180 degrees to the right into walking stance with right foot forward, right arm low block",
        "Left foot front snap kick, landing in front stance with left foot forward, left arm high punch",
        "Turn 90 degrees to the left into walking stance with left foot forward, left arm rising block",
        "Step forward into walking stance with right foot forward, right arm rising block",
        "Turn 270 degrees to the left into walking stance with left foot forward, right arm inner middle block",
        "Turn 180 degrees to the right into walking stance with right foot forward, left arm inner middle block",
        "Turn 90 degrees to the left into walking stance with left foot forward, left arm low block",
        "Right foot front snap kick, landing in walking stance with right foot forward, right arm middle punch",
        "Left foot front snap kick, landing in walking stance with left foot forward, left arm middle punch",
        "Right foot front snap kick, landing in walking stance with right foot forward, right arm middle punch KIHAP!",
        'When the Master says "Bah Ro", turn and face the Master.',
      ],
    };
  }
  if (pattern.number === 3) {
    return {
      description:
        "Taegeuk Sam Jang symbolizes the 'Ra', one of the 8 divination signs, which represent 'hot and bright'. This is to encourage the trainees to harbor a sense of justice and ardor for training.",
      steps: [
        "Start in the Ready or **Joon Bi** stance",
        "Turn left 90 degrees walking stance, left low block",
        "Right front kick landing in front stance, right punch, left punch",
        "Turn right 180 degrees walking stance, right low block",
        "Left front kick landing in front stance, left punch, right punch",
        "Turn left 90 degrees, left foot into walking stance, right knife hand strike to the neck",
        "Step forward into right walking stance, left knife hand strike to the neck",
        "Turn left 90 degrees, left knife hand middle block in back stance",
        "Adjust into front stance with a right middle punch",
        "Turn right 180 degrees, right knife hand middle block in back stance",
        "Adjust into front stance with a left middle punch",
        "Turn left 90 degrees, right inside block",
        "Step forward into right walking stance, left inside block",
        "Turn left 270 degrees walking stance, left low block",
        "Right front kick landing in front stance, right punch, left punch",
        "Turn right 180 degrees walking stance, right low block",
        "Left front kick landing in a left front stance, left punch, right punch",
        "Turn left 90 degrees into left walking stance, left low block, right middle punch",
        "Step forward into right walking stance, right low block, left middle punch",
        "Left front kick landing in a left walking stance, left low block, right middle punch",
        "Right front kick landing in a right walking stance, right low block, left middlepunch **KIHAP!**",
      ],
    };
  }
  if (pattern.number === 4) {
    return {
      description:
        "Taegeuk Sa Jang symbolizes the 'Jin', one of the 8 divination signs, which represents thunder meaning great power and dignity.",
      steps: [
        "Start in the Ready or **Joon Bi** stance",
        "Turn left 90 degrees, left double knife hand middle block in back stance",
        "Step forward, left downward parry (ending under elbow) with right fingertip thrust in front stance",
        "Turn right 180 degrees, right double knife hand middle block in back stance",
        "Step forward, right downward parry (ending under elbow) with left fingertip thrust in front stance",
        "Turn left 90 degrees, left knife hand high block and right knife hand strike (both at same time) in front stance",
        "Right front kick, left middle punch in front stance",
        "Left side kick",
        "Right side kick, right double knife hand middle block in back stance",
        "Turn left 270 degrees, left outside block in back stance",
        "Right front kick (land the leg back where it started), right inside block in back stance",
        "Turn right 180 degrees, right outside block in back stance",
        "Left front kick (land the leg back where it started), left inside block in back stance",
        "Turn left 90 degrees, left knife hand high block and right knife hand strike (both at same time) in front stance",
        "Right front kick, right back fist strike (to opponent's nose) in front stance",
        "Turn left 90 degrees, left inside block followed by a right middle punch in walking stance",
        "Turn right 180 degrees, right inside block followed by a left middle punch in walking stance",
        "Turn left 90 degrees, left inside block, right middle punch and then a left middle punch in front stance",
        "Step forward, right inside block, left middle punch and then a right middle punch in front stance **KIHAP!**",
      ],
    };
  }
  if (pattern.number === 5) {
    return {
      description:
        "Taegeuk Oh Jang symbolizes the 'Son', one of the 8 divination signs, which represent the wind, meaning both mighty force and calmness according to its strength and weakness.",
      steps: [
        "Start in the Ready or **Joon Bi** stance",
        "Turn left into front stance, left low block",
        "Left foot comes back to L shape stance straight legs, left hand hammer fist downwards",
        "Turn right into right front stance, right hand low block",
        "Right foot comes back to L shape stance straight legs, right hand hammer fist downwards",
        "Turn left 90* Advance forward into left front stance, left inside block, right inside block",
        "Right foot front kick into right front stance, right hand downward backfist to nose, left inside block",
        "Left foot front kick into left front stance, left hand downward backfist to nose, right inside block",
        "Advance right foot forward into right front stance, right hand downward backfist to nose",
        "Moving left foot, turn 270 degrees to the left into left back stance, knife hand middle block with left hand",
        "Step into right front stance, right elbow strike to the jaw",
        "Moving right foot, turn 180 degrees all the way to the right into right back stance, knife hand middle block",
        "Step into left front stance, left elbow strike to the jaw",
        "Turn left 90 degrees into left front stance, left hand low block, right inside block",
        "Right foot kick into right front stance, right hand low block, left inside block",
        "Turn left 90 degrees into left front stance, left high block",
        "Right side kick and right hand side hammer fist together into right front stance, left elbow into open right hand",
        "Turn right 180 degrees into right front stance, right high block",
        "Left side kick and left hand side hammer fist together into left front stance, right elbow strike into open left hand",
        "Turn left 90 degrees into left front stance, left low block, right inside block",
        "Right front kick, hop forward and land in a X stance while executing a right downward backfist to nose to the nose. The right foot is flat and the left foot should be on the ball of foot **KIHAP!**",
      ],
    };
  }
  if (pattern.number === 6) {
    return {
      description:
        "Taegeuk Yuk Jang symbolizes the 'Kam', one of the 8 divination signs, which represents water, meaning incessant flow and softness.",
      steps: [
        "Start in the Ready or **Joon Bi** stance",
        "Turn left 90 degrees into left front stance, left low block",
        "Right leg front kick, land back into left back stance and do a left outside block at same time",
        "Turn right 180 degree into right front stance, right low block",
        "Left front kick, land back into right back stance and do a right outside block at same time",
        "Turn left 90 degrees into a left front stance, right knife hand high block",
        "Right roundhouse kick and place kicking leg down a full step forward",
        "Turn left 90 degrees and step into a left front stance, left outside high block, right middle punch",
        "Right front kick and land in a right front stance, left middle punch",
        "Turn right 180 degrees into right front stance, right outside high block, left middle punch",
        "Left front kick landing in a left front stance, right middle punch",
        "Turn left 90 degrees into a Joon Bi/Ready stance, cross both hands in front of your chest and roll over each other downwards and do a slow double low block",
        "Step forward into a right front stance, left knife hand high block",
        "Left roundhouse kick and yell **KIHAP!**",
        "Place kicking leg down a full step forward, turn right 270 degrees into a right front stance, right low block",
        "Left front kick, land back into right back stance, right outside block",
        "Turn left 180 degrees into left front stance, left low block",
        "Right front kick, land back into left back stance, left outside block",
        "Move your right foot, stepping backwards, turn left 90 degrees into a left back stance, double knife hand middle block",
        "Stepping back into right back stance, double knife hand middle block",
        "Stepping back into left front stance, left middle palm block, right middle punch",
        "Stepping back into right front stance, right middle palm block, left middle punch",
        'When the Master says "**Bah Ro**", move your right foot back (in line with your left foot) and go into a Ready/**Joon Bi** stance',
      ],
    };
  }
  if (pattern.number === 7) {
    return {
      description:
        "Taegeuk Chil Jang symbolizes the 'Kan', one of the 8 divination signs, which represents the mountain, meaning ponder and firmness.",
      steps: [
        "Start in the Ready or **Joon Bi** stance",
        "Turn left 90 degrees into left cat stance, right crossing palm block",
        "Right front kick, return to left cat stance, left inside block",
        "Turn right 180 degrees into right cat stance, left crossing palm block",
        "Left front kick, return to cat stance, right inside block",
        "Turn left 90 degrees, move into left back stance, left double knife hand low block",
        "Step forward into right back stance moving left leg, right double knife hand low block",
        "Turn left 90 degrees, move into left cat stance, right crossing palm block, reverse backfist to nose (with same right hand), left fist remains palm down under right elbow",
        "Turn right 180 degrees into right cat stance, left crossing palm block, reverse backfist to nose (with same left hand), right fist remains palm down under left elbow",
        "Turn left 90 degrees, place left foot next to right foot, bring both hands into a chamber position on your belt with palms up, left hand grabs right fist at stomach level, slowly extend your arms outwards to chest level (attention stance or standing mediation)",
        "Step forward with left foot into left front stance, double scissor block (left arm does an outside block and right hand does a low block at same time), reverse scissor block (right outside block and left low block)",
        "Step forward with right foot into right foot stance, double scissor block (right outside block, left low block), reverse double scissor block (left outside block, right low block)",
        "Moving left foot, turn left 270 degrees into left front stance and do a double outside block (breaking your opponent's grip on your neck or shoulders)",
        "In front stance, reach up and grab head of your imaginary opponent with both hands, bring hands down & bring right knee up (knee strike to opponent's head), hop forward land with right foot with left foot crossed behind, throw a double upset punch (palm up) to opponent's ribs, move left leg back and go into right front stance, X low block",
        "Move right foot, turn right 180 degrees into right front stance, double outside block (breaking your opponent's grip on your neck or shoulders)",
        "In front stance, reach up and grab head of your imaginary opponent with both hands, bring hands down & bring left knee up (knee strike to opponent's head), hop forward land with left foot with right foot crossed behind, throw a double upset punch (palm up) to opponent's ribs, move right leg back and go into left front stance, X low block",
        "Turn left 90 degrees, move left foot into left walking stance, left backfist to temple (chamber backfist above right shoulder)",
        "Open left fist, right crescent kick slapping palm of left hand with the sole of your right foot, land in a right horse stance, right elbow strike into open at the last second palm of your left hand",
        "Move into right walking stance, right backfist to temple (chamber backfist above left shoulder)",
        "Open right fist, left crescent kick slapping palm of right hand with the sole of your left foot, land in a left horse stance, left elbow strike into open  at the last second palm of your right hand",
        "Stay in horse stance, left single knife hand middle block",
        "Step forward into right horse stance, right side punch **KIHAP!**",
      ],
    };
  }
  if (pattern.number === 8) {
    return {
      description:
        "Taegeuk Pal Jang symbolizes the 'Kon', one of the 8 divination signs, which represents 'Yin' and earth, meaning the root and settlement and also the beginning and the end.",
      steps: [
        "Start in the Ready or **Joon Bi** stance",
        "Step forward with left foot into left back stance, double fist middle block (guarding block)",
        "Adjust into left front stance, reverse middle punch with right hand",
        "Left double front kick **KIHAP!**, left inside block, double punch (right hand first)",
        "Step forward into right front stance, right middle punch",
        "Turn left 270 degrees, left low block and right single mountain high block (at same time), reverse front stance (bow stance), look towards direction of low block",
        "Move left foot into (forward) left front stance, grab imaginary opponent's collar with left hand & pull towards your right shoulder, slow right uppercut to imaginary opponent's chin",
        "Double step backwards (stepping left in front of right foot), turn your head 180 degrees, right low block and left single mountain high block (at same time), reverse front stance (bow stance), look towards direction of low block",
        "Move right foot into (forward) right front stance, grab imaginary opponent's collar with right hand & pull towards your left shoulder, slow left uppercut to imaginary opponent's chin",
        "Turn left 270 degrees, left double knife hand block in back stance",
        "Move left foot into left front stance, right middle punch",
        "Right front kick, return foot back to kicking position (behind left foot), step back with left foot, bring right foot into tiger/cat stance, right palm block",
        "Turn left 90 degrees, left foot in tiger/cat stance, left double knife hand middle block",
        "Left front kick, right middle punch in front stance",
        "Move left foot back into tiger/cat stance, left palm block",
        "Turn right 180 degrees, right foot into tiger/cat stance, right double knife hand middle block",
        "Right front kick, left middle punch in front stance",
        "Move right foot back into tiger/cat stance, palm block",
        "Turn right 90 degrees, right double fist low block (low guarding block) in back stance",
        "Left front kick, hold the chamber, jump right front kick **KIHAP!**, land in a right front stance, right inside block, double punch (left hand first)",
        "Turn left 270 degrees, left single knife hand middle block in back stance",
        "Move left foot into left front stance, reverse elbow strike to the jaw with right arm, reverse backfist to the nose with right fist, middle punch with left hand",
        "Move right foot and turn 180 degrees to the right into a right back stance, single knife hand middle block",
        "Move right foot into right front stance, reverse elbow strike to the jawwith left arm, reverse backfist to the nose with left fist, middle punch with right hand",
        'When Master yells "**Bah Ro**", move left foot, turn left 90 degrees and return to Ready/**Joon Bi** stance',
      ],
    };
  }
  return null;
};

const demoAliasesByPattern: Record<number, string[]> = {
  1: ["taeguek_1", "taeguek 1", "taegeuk_1", "taegeuk 1", "taeguk_1", "taeguk 1"],
  2: ["taeguek_2", "taeguek 2", "taegeuk_2", "taegeuk 2"],
  3: ["taeguek_3", "taeguek 3", "taegeuk_3", "taegeuk 3"],
  4: ["taeguek_4", "taeguek 4", "taegeuk_4", "taegeuk 4"],
  5: ["taeguek_5", "taeguek 5", "taegeuk_5", "taegeuk 5"],
  6: ["taeguek_6", "taeguek 6", "taegeuk_6", "taegeuk 6"],
  7: ["taeguek_7", "taeguek 7", "taegeuk_7", "taegeuk 7"],
  8: ["taeguek_8", "taeguek 8", "taegeuk_8", "taegeuk 8"],
};

function formatKihapParts(s: string): ReactNode {
  if (!s.includes("KIHAP")) return s;
  const parts = s.split(/(KIHAP!?)/gi);
  return parts.map((p, i) =>
    /^KIHAP/i.test(p) ? (
      <span key={i} className="font-semibold">
        {p}
      </span>
    ) : (
      <Fragment key={i}>{p}</Fragment>
    ),
  );
}

function RichInline({ text }: { text: string }) {
  if (!text.includes("**")) {
    return <>{formatKihapParts(text)}</>;
  }
  const segments = text.split(/(\*\*[^*]+\*\*)/g);
  return (
    <>
      {segments.map((seg, i) => {
        const m = seg.match(/^\*\*(.+)\*\*$/);
        if (m) {
          return (
            <strong key={i} className="font-semibold">
              {formatKihapParts(m[1])}
            </strong>
          );
        }
        return <Fragment key={i}>{formatKihapParts(seg)}</Fragment>;
      })}
    </>
  );
}

function TaeguekPatternInstructions({
  pattern,
  showPatternDemoButton,
  hasDemo,
  onWatchDemo,
  contentClassName,
}: {
  pattern: TaeguekPattern;
  showPatternDemoButton: boolean;
  hasDemo?: (aliases: string[]) => boolean;
  onWatchDemo?: (aliases: string[]) => void;
  contentClassName?: string;
}) {
  const instructions = getTaeguekInstructions(pattern);
  if (!instructions) return null;
  const aliases = demoAliasesByPattern[pattern.number];

  return (
    <div className={cn("space-y-4 pt-2", contentClassName)}>
      {showPatternDemoButton && aliases && hasDemo?.(aliases) && (
        <button
          type="button"
          className="inline-flex items-center text-sm font-medium text-taekwondo-blue hover:underline"
          onClick={() => onWatchDemo?.(aliases)}
          aria-label={`Watch Taegeuk ${pattern.number} demonstration video`}
        >
          Watch Demonstration Video
        </button>
      )}
      <p className="text-gray-700">
        <RichInline text={instructions.description} />
      </p>
      <div>
        <h4 className="mb-2 text-base font-medium text-taekwondo-red">Steps</h4>
        <ol className="list-none space-y-2">
          {instructions.steps.map((step, index) => (
            <li key={index} className="flex gap-2">
              <span className="shrink-0 font-semibold text-taekwondo-red tabular-nums">
                {index + 1}.
              </span>
              <span className="text-gray-700">
                <RichInline text={step} />
              </span>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}

function TaeguekLegacyLayout({
  selectedBelt,
  patterns,
  currentPattern,
  hasDemo,
  onWatchDemo,
}: {
  selectedBelt: BeltRank;
  patterns: TaeguekPattern[];
  currentPattern: number;
  hasDemo?: (aliases: string[]) => boolean;
  onWatchDemo?: (aliases: string[]) => void;
}) {
  return (
    <div className="space-y-4">
      <div>
        <h3 className="mb-2 text-lg font-semibold text-taekwondo-red">Required for Grading</h3>
        <p className="text-gray-700">{getGradingRequirements(selectedBelt)}</p>
      </div>

      <Accordion type="single" collapsible className="w-full">
        {patterns.map((pattern) => (
          <AccordionItem key={`pattern-${pattern.number}`} value={`pattern-${pattern.number}`}>
            <AccordionTrigger
              className={`hover:no-underline ${pattern.number === currentPattern ? "font-semibold text-taekwondo-blue" : ""}`}
            >
              <div className="flex w-full items-center text-left">
                <span className="flex-1">
                  {pattern.number}: {pattern.name} - {pattern.meaning}
                </span>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <TaeguekPatternInstructions
                pattern={pattern}
                showPatternDemoButton
                hasDemo={hasDemo}
                onWatchDemo={onWatchDemo}
              />
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}

function TaeguekCurriculumLayout({
  selectedBelt,
  patterns,
  currentPattern: _currentPattern,
  hasDemo,
  onWatchDemo,
  beltTabSurfaceClass,
}: {
  selectedBelt: BeltRank;
  patterns: TaeguekPattern[];
  currentPattern: number;
  hasDemo?: (aliases: string[]) => boolean;
  onWatchDemo?: (aliases: string[]) => void;
  beltTabSurfaceClass: string;
}) {
  const fingerprint = patterns.map((p) => p.number).join("|");

  const [activeTab, setActiveTab] = useState(() => String(patterns[0]?.number ?? 1));

  useEffect(() => {
    const nums = patterns.map((p) => p.number);
    setActiveTab(String(nums[0] ?? 1));
  }, [fingerprint]);

  const surface = beltTabSurfaceClass;

  return (
    <div className="space-y-4 text-left">
      <div>
        <h3 className="mb-2 text-lg font-semibold text-taekwondo-red">Required for Grading</h3>
        <p className="text-gray-700">{getGradingRequirements(selectedBelt)}</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full space-y-4">
        <ScrollableCurriculumTabStrip key={fingerprint} ariaLabelPrefix="Taegeuk forms">
          {patterns.map((pattern) => (
            <TabsTrigger
              key={pattern.number}
              value={String(pattern.number)}
              className={cn(
                "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-black/10 px-4 py-2 text-sm font-medium transition-shadow",
                surface,
                "data-[state=active]:z-10 data-[state=active]:shadow-md data-[state=active]:ring-2 data-[state=active]:ring-gray-900/40 data-[state=active]:ring-offset-2 data-[state=active]:ring-offset-gray-100",
              )}
            >
              {pattern.number}: {pattern.name}
            </TabsTrigger>
          ))}
        </ScrollableCurriculumTabStrip>

        {patterns.map((pattern) => (
          <TabsContent
            key={pattern.number}
            value={String(pattern.number)}
            className="mt-0 focus-visible:outline-none"
          >
            <div className="mt-2 rounded-lg bg-gray-50 p-4 md:p-5">
              <h3 className="mb-4 text-xl font-semibold text-taekwondo-red">
                {pattern.name} - {pattern.meaning}
              </h3>
              <TaeguekPatternInstructions
                pattern={pattern}
                showPatternDemoButton
                hasDemo={hasDemo}
                onWatchDemo={onWatchDemo}
                contentClassName="pt-0"
              />
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

export default function Taeguek({
  selectedBelt,
  getBeltColor: _getBeltColor,
  patterns,
  currentPattern,
  hasDemo,
  onWatchDemo,
  presentation = "legacy",
  beltTabSurfaceClass = DEFAULT_TAB_SURFACE,
}: TaeguekProps) {
  if (selectedBelt === "WHITE" || patterns.length === 0) {
    return null;
  }

  if (presentation === "curriculum") {
    return (
      <TaeguekCurriculumLayout
        selectedBelt={selectedBelt}
        patterns={patterns}
        currentPattern={currentPattern}
        hasDemo={hasDemo}
        onWatchDemo={onWatchDemo}
        beltTabSurfaceClass={beltTabSurfaceClass}
      />
    );
  }

  return (
    <TaeguekLegacyLayout
      selectedBelt={selectedBelt}
      patterns={patterns}
      currentPattern={currentPattern}
      hasDemo={hasDemo}
      onWatchDemo={onWatchDemo}
    />
  );
}
